import { createApp } from 'vue';
import * as Sentry from '@sentry/browser';
import 'bootstrap/js/dist/alert';
import 'bootstrap/js/dist/collapse';
import 'bootstrap/js/dist/dropdown';
import Modal from 'bootstrap/js/dist/modal';
import 'bootstrap/js/dist/tab';
import 'bootstrap/js/dist/toast';
import Toast from 'bootstrap/js/dist/toast';
import Tooltip from 'bootstrap/js/dist/tooltip';
import { createPinia } from 'pinia';
import { createRouter, createWebHistory } from 'vue-router';
import '@/components/otp-input.ts';
import '@/components/player-allowed-items-form';
import '@/components/player-form-modal';
import '@/components/player-search';
import '@/components/range-slider';

if (import.meta.env.VITE_SENTRY_DSN) {
    Sentry.init({
        dsn: import.meta.env.VITE_SENTRY_DSN,
        integrations: [Sentry.browserTracingIntegration()],
        tracesSampleRate: import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? 0.1,
        release: import.meta.env.VITE_SENTRY_RELEASE,
        ignoreErrors: ['Network Error'],
    });

    if (window.sentry_user) {
        Sentry.setUser(window.sentry_user);
    }
}

const clipboardToast = new Toast(
    document.querySelector('#clipboard-toast .toast')
);

function scrollIntoViewIfNeeded(target) {
    if (
        target.getBoundingClientRect().bottom > window.innerHeight ||
        target.getBoundingClientRect().top <
            document.querySelector('body > header').getBoundingClientRect()
                .bottom
    ) {
        target.scrollIntoView({ behavior: 'auto', block: 'center' });
    }
}

function positionAutoFillHeightElements() {
    for (const autoFillHeightElement of document.querySelectorAll(
        '.height-lg-auto-fill'
    )) {
        const offset =
            autoFillHeightElement.getBoundingClientRect().top -
            document.body.getBoundingClientRect().top;
        autoFillHeightElement.style.maxHeight =
            window.innerHeight - offset + 'px';
    }
}

window.addEventListener('resize', positionAutoFillHeightElements);
window.addEventListener('load', positionAutoFillHeightElements);

for (const tooltipTrigger of document.querySelectorAll(
    '[data-bs-toggle="tooltip"]'
)) {
    new Tooltip(tooltipTrigger, { trigger: 'hover' });
}

for (const collapseTrigger of document.querySelectorAll(
    '[data-bs-toggle="collapse"][data-bs-target]'
)) {
    const collapseTarget = document.querySelector(
        collapseTrigger.getAttribute('data-bs-target')
    );
    collapseTarget.addEventListener('shown.bs.collapse', function () {
        scrollIntoViewIfNeeded(collapseTarget);
    });
}

for (const openModal of document.querySelectorAll('[data-modal-open]')) {
    new Modal(openModal).show();
}

for (const modal of document.querySelectorAll('[data-reload-on-close]')) {
    modal.addEventListener('hide.bs.modal', function () {
        const overlay = document.createElement('div');
        overlay.id = 'reload-overlay';
        overlay.classList.add('modal-backdrop', 'show');
        overlay.style.backgroundColor = 'transparent';
        overlay.style.transition = 'background 0.5s';
        document.body.appendChild(overlay);
        setTimeout(() => overlay.classList.add('show'), 150);
        setTimeout(() => {
            const nestedModal = document.querySelector('.modal.show');
            const bodyOverlay = document.querySelector('#reload-overlay');
            if (nestedModal) {
                bodyOverlay.remove();
            } else {
                bodyOverlay.style.backgroundColor = '#fff';
                window.location.replace(window.location.href);
            }
        }, 300);
    });
}

for (const mobileModal of document.querySelectorAll('[data-mobile-modal]')) {
    const breakpoint = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue(
            '--bs-breakpoint-md'
        )
    );
    window.addEventListener('resize', () => {
        if (window.innerWidth > breakpoint) {
            Modal.getInstance(mobileModal)?.hide();
        }
    });
}

for (const link of document.querySelectorAll('[data-link]')) {
    link.addEventListener('click', function (element) {
        if (
            element.target !== element.currentTarget &&
            element.target.nodeName === 'A'
        ) {
            return;
        }
        window.location.href = link.getAttribute('data-link');
    });
}

for (const popupButton of document.querySelectorAll('[data-popup]')) {
    popupButton.addEventListener('click', function () {
        const url = popupButton.getAttribute('data-popup');
        window.open(
            url,
            'Print',
            'left=200, top=200, width=900, height=500, toolbar=0, resizable=0'
        );
    });
}

for (const printButton of document.querySelectorAll('[data-print]')) {
    printButton.addEventListener('click', function () {
        window.print();
    });
}

for (const uploadButton of document.querySelectorAll('[data-upload]')) {
    uploadButton.addEventListener('click', function () {
        const target = uploadButton.getAttribute('data-upload');
        document.querySelector(target).click();
    });
}

for (const replaceButton of document.querySelectorAll('[data-replace]')) {
    replaceButton.addEventListener('click', function () {
        const targets = replaceButton.getAttribute('data-replace');
        document
            .querySelectorAll(targets)
            .forEach(target => target.classList.remove('d-none'));
        replaceButton.classList.add('d-none');
    });
}

for (const deleteButton of document.querySelectorAll('[data-delete]')) {
    deleteButton.addEventListener('click', function () {
        const target = deleteButton.getAttribute('data-delete');
        setTimeout(() => document.querySelector(target).remove());
    });
}
for (const submitOnChangeElement of document.querySelectorAll(
    '[data-submit-on-change]'
)) {
    if (submitOnChangeElement.tagName === 'INPUT') {
        submitOnChangeElement.addEventListener('change', function () {
            submitOnChangeElement.closest('form').submit();
        });
        continue;
    }
    for (const input of submitOnChangeElement.querySelectorAll(
        'select, input[type=checkbox], input[type=radio], input[type=range], input[type=file]'
    )) {
        input.addEventListener('change', function () {
            submitOnChangeElement.submit();
        });
    }
    for (const input of submitOnChangeElement.querySelectorAll(
        'input[type=text], input[type="date"]'
    )) {
        input.addEventListener('blur', function () {
            submitOnChangeElement.submit();
        });
    }
}

for (const form of document.querySelectorAll('form')) {
    const requiredCheckboxes = form.querySelectorAll(
        '[data-required-at-least-one]'
    );
    if (requiredCheckboxes.length === 0) {
        continue;
    }

    form.addEventListener('submit', event => {
        const groups = {};
        for (const checkbox of requiredCheckboxes) {
            groups[checkbox.name] = true;
        }

        for (const name in groups) {
            const groupCheckboxes = form.querySelectorAll(
                `input[name="${name}"]`
            );
            const isChecked = [...groupCheckboxes].some(cb => cb.checked);
            if (!isChecked) {
                event.preventDefault();
                alert('Bitte wähle mindestens eine Option aus.');
                groupCheckboxes[0].focus();
                return;
            }
        }
    });
}

for (const disableOnSubmitElement of document.querySelectorAll(
    '[data-disable-on-submit]'
)) {
    disableOnSubmitElement.addEventListener('submit', event => {
        if (event.defaultPrevented) {
            return;
        }
        const button =
            event.submitter ??
            disableOnSubmitElement.querySelector('[type="submit"]');
        if (!button) {
            return;
        }
        button.disabled = true;
        button.classList.add('loading');
    });
}

for (const dateModificationButton of document.querySelectorAll(
    '[data-date-modification]'
)) {
    dateModificationButton.addEventListener('click', function () {
        const target = document.querySelector(
            dateModificationButton.getAttribute('data-date-modification-target')
        );
        const modification = dateModificationButton.getAttribute(
            'data-date-modification'
        );
        const number = parseInt(
            modification.charAt(0) + modification.replace(/[^0-9]/g, '')
        );
        let date = target.valueAsDate || new Date();
        if (modification.includes('months')) {
            date.setMonth(date.getMonth() + number);
        }
        target.valueAsDate = date;
    });
}

for (const clipboardButton of document.querySelectorAll('[data-clipboard]')) {
    clipboardButton.addEventListener('click', async function () {
        const text = clipboardButton.getAttribute('data-clipboard');

        if (navigator.clipboard) {
            await navigator.clipboard.writeText(text);
        } else {
            const textarea = document.createElement('textarea');
            textarea.setAttribute('readonly', true);
            textarea.setAttribute('contenteditable', true);
            textarea.style.position = 'fixed';
            textarea.value = text;
            document.body.appendChild(textarea);
            textarea.focus();
            textarea.select();
            const range = document.createRange();
            range.selectNodeContents(textarea);
            const selection = window.getSelection();
            selection.removeAllRanges();
            selection.addRange(range);
            textarea.setSelectionRange(0, textarea.value.length);

            document.execCommand('copy');
            textarea.remove();
        }
        clipboardToast.show();
    });
}

for (const selectBox of document.querySelectorAll('.select-box')) {
    for (const selectBoxItem of selectBox.querySelectorAll('[type="radio"]')) {
        selectBoxItem.addEventListener('change', function () {
            const label = selectBoxItem.closest('li').querySelector('label');
            const dropdown = selectBox.querySelector('.dropdown-toggle');
            dropdown.style.backgroundImage = label.style.backgroundImage;
            dropdown.querySelector('span').textContent = label.textContent;
        });
    }
}

for (const pictureSelector of document.querySelectorAll('.picture-selector')) {
    for (const pictureButton of pictureSelector.querySelectorAll(
        'button[data-picture-number]'
    )) {
        pictureButton.addEventListener('click', function () {
            pictureSelector.querySelector('input').value =
                pictureButton.dataset.pictureNumber;
            pictureSelector.querySelector(
                '.picture-selector-preview'
            ).style.backgroundImage = pictureButton.style.backgroundImage;
        });
    }
    for (const tabButton of pictureSelector.querySelectorAll(
        '[data-bs-toggle="tab"]'
    )) {
        tabButton.addEventListener('click', e => {
            e.stopPropagation();
        });
    }
}

for (const checkboxButton of document.querySelectorAll(
    'label button[type="submit"]'
)) {
    const checkbox = checkboxButton
        .closest('label')
        .querySelector('input[type="checkbox"]');
    if (checkbox) {
        checkboxButton.addEventListener('click', function () {
            checkbox.checked = true;
        });
    }
}

for (const select of document.querySelectorAll(
    'select[data-options-filter-function]'
)) {
    const controller = document.querySelector(
        select.getAttribute('data-options-filter-controller')
    );
    const options = select.querySelectorAll('option');
    const filter_function = eval(
        select.getAttribute('data-options-filter-function')
    );

    const filterOptions = () => {
        for (const option of options) {
            if (
                !option.value ||
                !controller.value ||
                filter_function(option, controller)
            ) {
                option.hidden = false;
                continue;
            }
            option.hidden = true;
            if (option.value === select.value) {
                select.value = [...options].find(
                    option => !option.hidden
                ).value;
            }
        }
    };
    filterOptions();
    controller.addEventListener('change', () => {
        filterOptions();
    });
}

const licenseGroupAuthorizationPdf = document.querySelector(
    '#license-group-authorization-pdf'
);

if (licenseGroupAuthorizationPdf) {
    (async () => {
        const { jsPDF } = await import('jspdf');
        const { default: html2canvas } = await import('html2canvas');

        const canvas = await html2canvas(licenseGroupAuthorizationPdf, {
            scale: 2,
        });
        const image = canvas.toDataURL('image/png');
        const pdf = new jsPDF({ format: 'a4', compress: true });
        const width = pdf.internal.pageSize.getWidth();
        const height = pdf.internal.pageSize.getHeight();
        pdf.addImage(image, 'PNG', 0, 0, width, height);
        pdf.save('Zugang.pdf');
    })();
}

const worksheet_generator = document.getElementById('worksheet-generator');
if (worksheet_generator) {
    Sentry.setTag('app', 'WorksheetGenerator');
    (async () => {
        const { default: WorksheetGenerator } =
            await import('@/components/WorksheetGenerator.vue');
        createApp(WorksheetGenerator, worksheet_generator.dataset)
            .use(createPinia())
            .mount(worksheet_generator);
    })();
}

const schedule_sheet_generator = document.getElementById(
    'schedule-sheet-generator'
);
if (schedule_sheet_generator) {
    Sentry.setTag('app', 'ScheduleSheetGenerator');
    (async () => {
        const { default: ScheduleSheetGenerator } =
            await import('@/components/ScheduleSheetGenerator.vue');
        createApp(
            ScheduleSheetGenerator,
            schedule_sheet_generator.dataset
        ).mount(schedule_sheet_generator);
    })();
}

const workbook_viewer = document.getElementById('workbook-viewer');
if (workbook_viewer) {
    (async () => {
        const { default: WorkbookViewer } =
            await import('@/Pages/WorkbookViewer.vue');
        createApp(WorkbookViewer, {
            items: workbook_viewer.dataset.items,
        }).mount(workbook_viewer);
    })();
}

const shipping_manager = document.getElementById('shipping-manager');
if (shipping_manager) {
    Sentry.setTag('app', 'ShippingManager');
    (async () => {
        const { default: ShippingManager } =
            await import('@/Pages/ShippingManager/Index.vue');
        createApp(ShippingManager)
            .use(
                createRouter({
                    history: createWebHistory(),
                    routes: [
                        {
                            name: 'shipping_order_list',
                            path: '/versandmanager/',
                            component: () =>
                                import('@/Pages/ShippingManager/ShippingOrderList.vue'),
                        },
                        {
                            name: 'shipping_order_packaging',
                            path: '/versandmanager/bestellung_verpacken/:shipping_order_id',
                            component: () =>
                                import('@/Pages/ShippingManager/ShippingOrderPackaging.vue'),
                        },
                        {
                            name: 'sipping_products',
                            path: '/versandmanager/produkte',
                            component: () =>
                                import('@/Pages/ShippingManager/ShippingProducts.vue'),
                        },
                    ],
                })
            )
            .use(createPinia())
            .mount(shipping_manager);
    })();
}
